import { useState, useEffect, useCallback, type RefObject } from 'react'

interface UseBackgroundContrastOptions {
  containerRef?: RefObject<HTMLElement | null>
}

// Primary brand color: #2f2981 (RGB: 47, 41, 129)
const PRIMARY_COLOR = { r: 47, g: 41, b: 129 }
const COLOR_TOLERANCE = 40 // Tolerance for matching primary color

/**
 * Checks if a color is similar to the primary brand color
 */
function isPrimaryColor(r: number, g: number, b: number): boolean {
  return (
    Math.abs(r - PRIMARY_COLOR.r) <= COLOR_TOLERANCE &&
    Math.abs(g - PRIMARY_COLOR.g) <= COLOR_TOLERANCE &&
    Math.abs(b - PRIMARY_COLOR.b) <= COLOR_TOLERANCE
  )
}

/**
 * Detects whether the background at a specific point is dark, light, or primary.
 * Uses document.elementsFromPoint to find the actual visible background element
 * by looking through all elements at that point.
 */
function detectBackgroundAtPoint(x: number, y: number, excludeElement?: HTMLElement): 'dark' | 'light' | 'primary' {
  // Get all elements at this point
  const elements = document.elementsFromPoint(x, y) as HTMLElement[]
  
  // Find the first element that is not the excluded one (the button itself)
  for (const element of elements) {
    if (excludeElement && (element === excludeElement || excludeElement.contains(element))) {
      continue
    }
    
    // Walk up from this element to find a background color
    let current: HTMLElement | null = element
    while (current) {
      const style = getComputedStyle(current)
      const bgColor = style.backgroundColor
      
      if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        const match = bgColor.match(/\d+/g)
        if (match && match.length >= 3) {
          const [r, g, b] = match.map(Number)
          
          // Check if it's the primary color first
          if (isPrimaryColor(r, g, b)) {
            return 'primary'
          }
          
          // Calculate luminance
          const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
          return luminance < 0.5 ? 'dark' : 'light'
        }
      }
      current = current.parentElement
    }
  }

  // Default to light if no background found
  return 'light'
}

/**
 * Detects whether the background behind a given element is dark, light, or primary.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>,
  options: UseBackgroundContrastOptions = {}
): 'dark' | 'light' | 'primary' {
  const [background, setBackground] = useState<'dark' | 'light' | 'primary'>('light')

  const checkBackground = useCallback(() => {
    const element = elementRef.current
    if (!element) return

    const rect = element.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    // Skip if element is not in viewport
    if (centerY < 0 || centerY > window.innerHeight || centerX < 0 || centerX > window.innerWidth) {
      return
    }

    // Sample the background at the center of the element
    const bg = detectBackgroundAtPoint(centerX, centerY, element)
    setBackground(bg)
  }, [elementRef])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // Initial check
    const timer = setTimeout(checkBackground, 100)

    // Check on scroll and resize
    const handleScroll = () => {
      window.requestAnimationFrame(checkBackground)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', checkBackground, { passive: true })

    // Use intersection observer to check when element is visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            checkBackground()
          }
        })
      },
      { threshold: [0, 0.5, 1] }
    )
    
    observer.observe(element)

    return () => {
      clearTimeout(timer)
      observer.disconnect()
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', checkBackground)
    }
  }, [elementRef, checkBackground, options.containerRef])

  return background
}
